<template>
    <div>
        <div class="ma-5 text-center" v-if="isLoading">
            <v-progress-circular class="mt-8" indeterminate :width="2"></v-progress-circular>
        </div>
        <v-form v-on:submit.prevent="submit" ref="form" v-else>


            <v-card outlined elevation="0" class="mb-4">
                <v-tabs v-model="tab" grow>
                    <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                        <img :src="lang.icon" style="max-height: 20px" />
                        &nbsp;&nbsp;{{ lang.name }}
                        &nbsp;
                        <v-icon v-if="langOk(category.translations, lang.code, [
                            'title',
                        ])
                        " color="success">
                            {{ icons.mdiCheck }}
                        </v-icon>
                        <v-icon v-else color="error">
                            {{ icons.mdiBlockHelper }}
                        </v-icon>
                    </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                    <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                        <div style="padding: 20px 20px 0 20px">
                            <img :src="lang.icon" style="max-height: 14px" />
                            <br />
                            <br />

                            <v-text-field :id="'category-title-' + lang.code"
                                v-model="category.translations[lang.code].title" :label="$t('Palautetyypin nimi')"
                                outlined class="mb-4" hide-details="auto" :rules="[required]" />

                            <v-text-field outlined v-model="category.translations[lang.code].description"
                                maxlength="100"
                                :label="$t('Lyhyt kuvaus (milloin palautetyyppiä käytetään)')"></v-text-field>

                            <v-textarea outlined rows="4" v-model="category.translations[lang.code].guide"
                                :label="$t('Ohje palautteen lähettäjälle (esim. mitä tietoja ilmoitukseen tulee lisätä)')"
                                o></v-textarea>

                            <v-switch v-model="category.is_active" :label="$t('Käytössä')"></v-switch>

                            <v-switch v-model="category.use_form" :label="$t('Käytä mukautettua lomaketta')"
                                persistent-hint class="mt-4">
                            </v-switch>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
                <v-card-text class="mt-0 pt-0">
                    <v-input class="mb-3 mt-3" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
                    <!-- Form Errors Alert -->
                    <template v-if="formErrors.length > 0">
                        <div class="v-messages theme--light error--text mb-3" role="alert">
                            <div class="v-messages__wrapper">
                                <div class="v-messages__message" v-for="(error, index) in formErrors" :key="index">
                                    {{ error }}
                                </div>
                            </div>
                        </div>

                    </template>

                    <v-btn type="submit" color="primary">{{ $t('Tallenna') }}</v-btn>
                </v-card-text>
            </v-card>

            <!-- Form Editor Section - only shown when use_form is enabled -->
            <v-card v-if="category.use_form" outlined elevation="0" class="mt-4">
                <v-card-title>
                    <v-icon left>{{ icons.mdiFormSelect }}</v-icon>
                    {{ $t('Lomakkeen kentät') }}
                </v-card-title>
                <v-card-text>
                    <!-- Warning when form editing is not allowed (category was active when loaded) -->
                    <v-alert v-if="!canEditForm" type="info" dense class="mb-4">
                        <v-icon left small>{{ icons.mdiLock }}</v-icon>
                        {{ $t('Poista palautetyyppi käytöstä ja tallenna muutokset muokataksesi lomakekenttiä') }}
                    </v-alert>

                    <!-- Form Editor - always shown, but in readonly mode when category was active when loaded -->
                    <FormEditor ref="formEditor" :form-data="category.form || { fields: [] }"
                        :languages="tenant.languages" :has-submissions="hasFormSubmissions" :fields-only="true"
                        :readonly="!canEditForm" @update:form="onFormUpdate" />
                </v-card-text>
            </v-card>
        </v-form>
    </div>
</template>

<script>
import { getValidTranslations, langOk } from '@/@core/utils/translation_helpers';
import { getFeedbackCategory } from '@/api/admin/feedback.admin.api';
import useAuth from '@/useAuth';
import { onMounted, reactive, ref } from '@vue/composition-api';
import { mdiCheck, mdiBlockHelper, mdiFormSelect, mdiLock } from '@mdi/js';
import { i18n } from '@/plugins/i18n';
import adminApi from '@/api/admin';
import { required } from '@/@core/utils/validation';
import FormEditor from '@/components/forms/FormEditor.vue';

export default {
    props: ["id"],
    components: {
        FormEditor,
    },
    setup(props, { emit }) {
        const { tenant } = useAuth();
        const tab = ref(null);
        const form = ref(null);
        const formEditor = ref(null);
        const formErrors = ref([]);
        const isLoading = ref(false);

        const category = reactive({
            id: null,
            translations: {},
            is_active: true,
            ui_order: 1,
            use_form: false,
            form: null
        });

        const hasFormSubmissions = ref(false);
        // Stores whether form can be edited - determined by is_active state when loaded from backend
        // For new categories, this is true. For existing, it's based on the loaded is_active state.
        const canEditForm = ref(true);

        tenant.value.languages.forEach((lang) => {
            category.translations[lang.code] = {
                title: "",
                description: "",
                guide: "",
            };
        });

        const getFeedbackCategory = async (id) => {
            isLoading.value = true;
            const response = await adminApi.getFeedbackCategory(id);
            if (response.ok) {
                category.id = response.data.id;
                category.is_active = response.data.is_active;
                category.ui_order = response.data.ui_order;
                category.use_form = response.data.use_form || false;
                category.form = response.data.form || null;

                // Set canEditForm based on the loaded is_active state
                // Form can only be edited when category was inactive when loaded
                canEditForm.value = !response.data.is_active;

                // Check if form has submissions (for locking edits)
                if (category.form && category.form.id) {
                    hasFormSubmissions.value = (category.form.submission_count || 0) > 0;
                }

                category.translations = {};
                tenant.value.languages.forEach((lang) => {
                    if (response.data.translations[lang.code]) {
                        category.translations[lang.code] = response.data.translations[lang.code];
                    } else {
                        category.translations[lang.code] = {
                            title: "",
                            description: "",
                            guide: "",
                        };
                    }
                });
            }
            else {
                alert(response.error);
            }
            isLoading.value = false;
        }

        const onFormUpdate = (formData) => {
            category.form = formData;
        }

        const submit = async () => {
            formErrors.value = [];
            var valid = form.value.validate();

            // Validate form fields if use_form is enabled
            if (category.use_form && formEditor.value) {
                const fieldErrors = formEditor.value.validateFields();
                if (fieldErrors.length > 0) {
                    formErrors.value = fieldErrors.map(e => `${e.identifier} ${e.message}`);
                    valid = false;
                }
            }

            if (valid) {
                const saveCategoryRequest = Object.assign({}, category);

                // Translations
                saveCategoryRequest.translations = getValidTranslations(category.translations, [
                    "title",
                ]);

                // Include use_form and form in save request
                saveCategoryRequest.use_form = category.use_form;
                if (category.use_form && category.form) {
                    saveCategoryRequest.form = category.form;
                } else if (!category.use_form) {
                    // Keep form data but don't use it
                    saveCategoryRequest.form = category.form;
                }

                var res = await adminApi.saveFeedbackCategory(saveCategoryRequest);
                if (res.ok) {
                    if (canEditForm.value == false && saveCategoryRequest.is_active == false && saveCategoryRequest.use_form == true) {
                        getFeedbackCategory(props.id);
                    }
                    else {
                        emit('saved', res.data.id);
                    }
                } else {
                    alert(res.error);
                }
            }
        }

        onMounted(() => {
            if (props.id) {
                getFeedbackCategory(props.id);
            }
        });

        const validateLangs = () => {
            let valid = true;
            tenant.value.languages.forEach((lang) => {
                if (!langOk(category.translations, lang.code, ["title"]))
                    valid = false;
            });

            if (valid) {
                return true;
            } else {
                return i18n.t("Otsikko on pakollinen kaikilla kielillä");
            }
        };

        return {
            tab,
            form,
            formEditor,
            formErrors,
            category,
            tenant,
            validateLangs,
            langOk,
            isLoading,
            submit,
            required,
            hasFormSubmissions,
            canEditForm,
            onFormUpdate,
            icons: {
                mdiCheck,
                mdiBlockHelper,
                mdiFormSelect,
                mdiLock
            }
        }
    }
}
</script>

<style lang="scss" scoped></style>